import { useSetAtom, useAtomValue, useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import FilterList from '@mui/icons-material/FilterList';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { poiState, PointOfInterestTab } from '@/components/Analysis/modules/pointOfInterest';
import { EquipmentsTab } from '@/components/Analysis/shared/EquipmentsTab';
import * as analysisState from '@/components/Analysis/state';
import {
  DrawersProps,
  PointOfInterestChecked,
  PointOfInterestWithCuboids,
  SelectedTab,
} from '@/types';
import { SideDrawer } from '@/components/shared/SideDrawer';
import useAbility from '@/hooks/useAbility';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { useAnalysisCuboidActions } from '../../modules/cuboids/hooks/useAnalysisCuboidActions';
import { abyssColors } from '@/theme/colors';
import { CustomBlocksTab } from '../../modules/CustomBlocks';
import { useFeatureFlag } from '@/hooks';
import { TabType } from '@/components/shared/BasicTabs/BasicTabs';
import * as customBlocksState from '@/components/Analysis/modules/CustomBlocks/state';
import * as customBlockState from '@/components/Analysis/modules/CustomBlocks/state';
import { FloatingLine, tabsStyles } from './styles';
import { ComponentsTab } from '../../ReviewScreen/ComponentsTab';

export const AssemblyTabs = ({ allClosed, isOpen, handleOpen }: DrawersProps) => {
  const setFilteredAssemblyIds = useSetAtom(analysisState.filteredAssemblyIds);
  const [resetWorkscopeFocus, setWorkscopeResetFocus] = useState<boolean>(true);
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);

  const allPOI = useAtomValue(poiState.structurePois);
  const isAlphaUser = useFeatureFlag('alpha-user');

  const blisterState = useAtomValue(poiState.blisterToAdd);

  const selectedAssemblyId = useAtomValue(analysisState.selectedAssemblyId);
  const filters = useAtomValue(analysisState.filters);
  const selectedSavedBlockId = useAtomValue(customBlocksState.selectedSavedBlockId);
  const selectedCustomBlock = useAtomValue(customBlockState.selectedCustomBlock);
  const selectedSavedBlock = useAtomValue(customBlockState.selectedSavedBlock);
  const filteredAssemblies = useAtomValue(analysisState.filteredAssemblies);

  const [poiFilteredByCheckbox, setPoiFilteredByCheckbox] = useAtom(poiState.poiFilteredByCheckbox); // const filteredAssemblies = useAtomValue(analysisState.filteredAssembliesByTemplateFields);

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(SelectedTab.COMPONENT);
  const { ability } = useAbility();
  const canReadPOI = ability.can('read', 'PointOfInterest');

  const { initializeCuboids } = useAnalysisCuboidActions();

  const selectedPointOfInterest = useAtomValue(poiState.selectedPointOfInterestId);

  const canViewReviewScreen = useFeatureFlag(['api-inspector', 'mi-engineer']);

  useEffect(() => {
    if (selectedPointOfInterest) {
      setSelectedTabIndex(SelectedTab.POINTS_OF_INTEREST);
    }
  }, [selectedPointOfInterest]);

  const changeTab = useCallback(
    (tabIndex: number) => {
      setWorkscopeResetFocus(selectedTabIndex !== SelectedTab.POINTS_OF_INTEREST);
      setSelectedTabIndex(tabIndex);
    },
    [selectedTabIndex]
  );

  useEffect(() => {
    if (selectedSavedBlockId) {
      setSelectedTabIndex(SelectedTab.CUSTOM_BLOCKS);
    }
  }, [selectedSavedBlockId]);

  const { rightDrawerWidth, doublePanelWidth } = useDrawerWidths();

  const drawerWidth = useMemo(() => {
    if (editPointOfInterest?.formState === 'Verify') return rightDrawerWidth;
    return blisterState?.state === 'UpdatingBlister' ? doublePanelWidth : rightDrawerWidth;
  }, [blisterState?.state, doublePanelWidth, rightDrawerWidth, editPointOfInterest?.formState]);

  // Only get the POIs that exists on the filtered assemblies, exluding the ones that are not in the filtered assemblies
  const filteredPois = useMemo(() => {
    if (!allPOI || allPOI.length === 0) {
      return [];
    }

    const { paintRegionIds, workpackIds } = filters ?? {};
    const paintRegionSet =
      paintRegionIds && paintRegionIds.length > 0 ? new Set(paintRegionIds) : undefined;
    const workpackSet = workpackIds && workpackIds.length > 0 ? new Set(workpackIds) : undefined;
    const filteredAssemblySet = new Set(filteredAssemblies?.map(({ id }) => id));
    return allPOI.filter((poi) => {
      return (
        (filteredAssemblySet.has(poi.assembly.id) &&
          (!paintRegionSet || !poi.paintRegion?.id || paintRegionSet?.has(poi.paintRegion?.id)) &&
          (!workpackSet || (poi.workpack?.id && workpackSet?.has(poi.workpack?.id)))) ||
        (selectedSavedBlock &&
          poi.paintRegions?.some((paintRegion) => paintRegion.id === selectedSavedBlock.id)) ||
        (selectedCustomBlock &&
          poi.paintRegions?.some(
            (paintRegion) => paintRegion.id === selectedCustomBlock.paintRegionId
          ))
      );
    });
  }, [allPOI, filteredAssemblies, filters, selectedSavedBlock, selectedCustomBlock]);

  // If an assembly is selected, only show the POIs for that assembly
  // otherwise show all POIs for the filtered assemblies
  const pois = useMemo(() => {
    // If an assembly is selected, filter POIs for that assembly or for the selected block(s)
    if (selectedAssemblyId || selectedCustomBlock || selectedSavedBlock) {
      return filteredPois.filter((poi) => {
        const isPoiInSelectedAssembly = selectedAssemblyId === poi.assembly.id;

        const isPoiInSelectedSavedBlock =
          selectedSavedBlock &&
          poi.paintRegions?.some((paintRegion) => paintRegion.id === selectedSavedBlock.id);

        const isPoiInSelectedCustomBlock =
          selectedCustomBlock &&
          poi.paintRegions?.some(
            (paintRegion) => paintRegion.id === selectedCustomBlock.paintRegionId
          );

        return isPoiInSelectedAssembly || isPoiInSelectedSavedBlock || isPoiInSelectedCustomBlock;
      });
    }

    // If no assembly is selected, return all filtered POIs
    return filteredPois;
  }, [filteredPois, selectedAssemblyId, selectedSavedBlock, selectedCustomBlock]);

  const tabs = useMemo(() => {
    return [
      {
        index: SelectedTab.COMPONENT,
        label: 'Component',
        component: canViewReviewScreen ? (
          <ComponentsTab changeTab={changeTab} />
        ) : (
          <EquipmentsTab changeTab={changeTab} />
        ),
      },
      isAlphaUser
        ? {
            index: SelectedTab.CUSTOM_BLOCKS,
            label: 'Blocks',
            component: <CustomBlocksTab resetFocus={resetWorkscopeFocus} />,
          }
        : undefined,
      {
        index: SelectedTab.POINTS_OF_INTEREST,
        label: 'Points of interest',
        component: (
          <PointOfInterestTab pois={pois as PointOfInterestChecked[]} changeTab={changeTab} />
        ),
      },
    ]
      .filter(Boolean)
      .filter((item) => item?.label !== 'Points of Interest' || canReadPOI) as TabType[];
  }, [canViewReviewScreen, changeTab, isAlphaUser, resetWorkscopeFocus, pois, canReadPOI]);

  useEffect(() => {
    // initialise the poiFilteredByCheckbox with all pois for this equipment/assembly
    // when an assembly is selected
    setPoiFilteredByCheckbox(pois as PointOfInterestChecked[]);
  }, [pois, setPoiFilteredByCheckbox]);

  useEffect(() => {
    if (poiFilteredByCheckbox) {
      initializeCuboids(poiFilteredByCheckbox as PointOfInterestWithCuboids[]);
    }
    // Adding initializeCuboids to the dependency array causes an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poiFilteredByCheckbox]);

  useEffect(() => {
    const filteredAssemblyIds = filteredAssemblies ? filteredAssemblies.map(({ id }) => id) : [];

    // this line is all that needs to be set to highlight the asset results in the spacial viewer
    // feel free to change if needed
    setFilteredAssemblyIds(filteredAssemblyIds);
  }, [filteredAssemblies, setFilteredAssemblyIds]);

  return (
    <SideDrawer
      allDrawersClosed={allClosed}
      icon={<FilterList />}
      position="right"
      width={drawerWidth}
      isOpen={isOpen}
      handleOpen={handleOpen}
      iconStyles={{ top: 10, zIndex: '102 !important' }}
      sxPaper={{ overflowY: 'hidden', border: 0 }}
    >
      <BasicTabs
        tabs={tabs}
        selectedTab={selectedTabIndex}
        tabsBackgroundColor={
          canViewReviewScreen ? abyssColors.primary[50] : abyssColors.neutral[50]
        }
        changeTab={changeTab}
        tabsStyles={tabsStyles}
        customSeperator={<FloatingLine />}
        fullWidth
        // renderingStrategy={canViewReviewScreen ? 'eager' : 'lazy'}
      />
    </SideDrawer>
  );
};
